'use client';

import {
  Banner,
  Breakpoint,
  Button,
  DialogBody,
  DialogFooter,
  ErrorState,
  Spinner,
  Stack,
  Text,
} from '@pie/amber';
import { PaymentMethodType, ZuoraSession } from '@/lib/api/billing';
import { useZuoraHostedPage, type ZuoraFailureReport } from '@/lib/zuora/useZuoraHostedPage';
import { AffectedPolicy } from '../AffectedPolicy';
import { PaymentAcknowledgment } from '../PaymentAcknowledgment';
import { PaymentMethodTypeSelector } from '../PaymentMethodTypeSelector';
import { ZuoraError } from '@/lib/zuora/classifyZuoraError';
import { reporter } from '@upms/observability';
import { useCallback, useEffect, useState } from 'react';
import { usePaymentOutcome } from '@/components/PaymentDialog/PaymentOutcomeContext';
import { toZuoraErrorCategory } from '@/lib/analytics/paymentErrorCategory';
import { PaymentMethodUiType, toApiPaymentMethodType } from '../utils';

/**
 * Zuora payment-method update flow — dialog *content* only; the parent
 * (`PaymentDialog`) owns the shell and policy fetch.
 *
 * Zuora's HPM 2.0 lib (`Z.renderWithErrorHandler`) looks up the container by a
 * hardcoded id — `zuora_payment`. Renaming breaks the integration silently: no
 * iframe, no error; the render timeout in `useZuoraHostedPage` is the only signal.
 */
const CONTAINER_ID = 'zuora_payment';

export type ZuoraPaymentSessionState = {
  isLoading: boolean;
  data: ZuoraSession | null;
};

export type ZuoraPaymentDialogProps = {
  session: ZuoraPaymentSessionState;
  policyId: string;
  policyNumber: string;
  paymentMethodType: PaymentMethodType;
  onPaymentMethodTypeChange: (methodType: PaymentMethodType) => void;
  /** Refetch the payment session (mint a fresh token) to retry after an error. */
  onRequestNewSession: () => void;
  onClose: () => void;
  /** Fired once Zuora confirms success; the shell starts the replication wait before revealing the success banner. */
  onSuccess: () => void;
  /** TODO(EE-643): temporary stopgap — true while waiting for Zuora→Billing replication; suppresses the success view. */
  isAwaitingReplication?: boolean;
  /**
   * Optional caller UI hook, invoked with the raw Zuora message on every
   * failure. Datadog reporting is separate — an absent `onError` never
   * suppresses observability.
   */
  onError?: (message: string) => void;
  hasExistingPaymentMethod?: boolean;
};

const ZuoraDialogReplicationBody = () => (
  <DialogBody>
    <Stack align="center" className="pb-layout-xl pt-layout-xl" direction="column" gap="2xs">
      <Spinner className="text-text-subtle-default" size="lg" title="Please wait" />
      <Text emphasis="subtle" variant="body-xs">
        Updating payment method
      </Text>
    </Stack>
  </DialogBody>
);

const ZuoraDialogSuccessBody = ({ onClose }: { onClose: ZuoraPaymentDialogProps['onClose'] }) => {
  return (
    <>
      <DialogBody>
        <Banner header="Payment Method Added" size="lg" tone="positive">
          Your payment method on file has been updated.
        </Banner>
      </DialogBody>
      <Breakpoint above="sm">
        <DialogFooter>
          <Stack direction="row" gap="sm" justify="end">
            <Button
              emphasis="heavy"
              fluid={{ base: true, sm: false }}
              onClick={onClose}
              size={{ base: 'lg', sm: 'md' }}
              tone="accent"
            >
              Done
            </Button>
          </Stack>
        </DialogFooter>
      </Breakpoint>
    </>
  );
};

const ZuoraDialogErrorBody = ({
  policyNumber,
  error,
  onClose,
}: {
  policyNumber: string | undefined;
  error: ZuoraError;
  onClose: ZuoraPaymentDialogProps['onClose'];
}) => {
  return (
    <>
      <DialogBody>
        <ErrorState heading={error.title} helpText={error.body} size="md" />
      </DialogBody>
      {policyNumber && <AffectedPolicy policyNumber={policyNumber} />}
      <Breakpoint above="sm">
        <DialogFooter>
          <Stack direction="row" gap="sm" justify="end">
            <Button
              data-testid="zuora-error-close-button"
              emphasis="heavy"
              fluid={{ base: true, sm: false }}
              onClick={onClose}
              size={{ base: 'lg', sm: 'md' }}
              tone="accent"
            >
              Close
            </Button>
          </Stack>
        </DialogFooter>
      </Breakpoint>
    </>
  );
};

export const ZuoraPaymentDialogContent = ({
  session,
  policyId,
  policyNumber,
  paymentMethodType,
  onPaymentMethodTypeChange,
  onRequestNewSession,
  onClose,
  onSuccess,
  onError,
  hasExistingPaymentMethod = true,
  isAwaitingReplication,
}: ZuoraPaymentDialogProps) => {
  const [methodType, setMethodType] = useState<PaymentMethodUiType>(
    paymentMethodType === 'card' ? 'credit' : 'bank'
  );
  const { report: reportOutcome } = usePaymentOutcome();

  useEffect(() => {
    reportOutcome({ provider: 'zuora' });
  }, [reportOutcome]);

  // Reports every failure at the severity classifyZuoraError assigned — see that
  // file for the matrix, and docs/agents/observability.md for the rule. The raw
  // Zuora message is the log message (prefixed "Zuora Error:"); the error-response
  // data rides as searchable attributes. `onError` is a caller UI hook only — an
  // absent one never suppresses reporting.
  const reportFailure = useCallback(
    (report: ZuoraFailureReport) => {
      const message = `Zuora Error: ${report.message}`;
      const context = {
        feature: 'zuora',
        operation: 'payment-method-update',
        code: report.code ?? 'none',
        responseFrom: report.responseFrom ?? 'none',
        policyId,
        paymentMethodType,
      };
      if (report.logLevel === 'error') {
        reporter.error(message, context);
      } else {
        reporter.info(message, context);
      }
      onError?.(report.message);
      reportOutcome({
        errorCategory: toZuoraErrorCategory({
          code: report.code,
          responseFrom: report.responseFrom,
        }),
      });
    },
    [onError, policyId, paymentMethodType, reportOutcome]
  );

  const { status, error, submit } = useZuoraHostedPage({
    session: session.isLoading ? null : session.data,
    containerId: CONTAINER_ID,
    // Bubble success to the parent (which refreshes the policy billing data) as
    // soon as Zuora confirms. The refId is unused here — the dialog owns the
    // whole success UX.
    onSuccess: () => {
      reporter.action('payment_method_updated', {
        feature: 'zuora',
        operation: 'payment-method-update',
        policyId,
        paymentMethodType,
      });
      onSuccess();
    },
    onFailure: reportFailure,
    // Recoverable submit failures re-mint via this callback (single-use token,
    // capped). Terminal errors don't call it.
    onRequestNewSession,
  });

  const onMethodTypeChange = (methodType: PaymentMethodUiType) => {
    setMethodType(methodType);
    onPaymentMethodTypeChange(toApiPaymentMethodType(methodType));
  };

  if (status === 'success') {
    return isAwaitingReplication ? (
      <ZuoraDialogReplicationBody />
    ) : (
      <ZuoraDialogSuccessBody onClose={onClose} />
    );
  }

  // Terminal dead-end: request-page / load-timeout failures immediately, or
  // re-mint failures once the retry cap is exhausted. Recoverable submit failures
  // stay in the form view below with the banner above the re-rendered form.
  if (status === 'error' && error) {
    return <ZuoraDialogErrorBody policyNumber={policyNumber} error={error} onClose={onClose} />;
  }

  const isLoading = status === 'loading' || session.isLoading;

  return (
    <>
      <DialogBody data-testid="zuora-dialog-body">
        <Stack gap="layout-md">
          {/*
           * Persists the last classified error above the form during recovery,
           * so the user sees what failed AND gets a form to retry. Clears on
           * submit success. Once the re-mint cap is exhausted (or a terminal
           * error occurs) status flips to 'error' and the dead-end body renders.
           */}
          {error && <ErrorState heading={error.title} helpText={error.body} size="md" />}
          {policyNumber && <AffectedPolicy policyNumber={policyNumber} />}
          <PaymentMethodTypeSelector value={methodType} onValueChange={onMethodTypeChange} />
          {isLoading && (
            <Stack
              align="center"
              className="pb-layout-xl pt-layout-xl"
              direction="column"
              gap="2xs"
            >
              <Spinner className="text-text-subtle-default" size="lg" title="Payment form" />
              <Text emphasis="subtle" variant="body-xs">
                Loading
              </Text>
            </Stack>
          )}
          {/*
           * Container MUST stay mounted in the form view — the hook renders the
           * iframe into it by the hardcoded `zuora_payment` id. Hidden while
           * `loading` so the spinner spans the wait with no half-rendered iframe
           * flashing through; revealed on `ready` (and `submitting`).
           */}
          {!session.isLoading && (
            <div className={isLoading ? 'invisible' : 'w-full'} id={CONTAINER_ID} />
          )}
          <PaymentAcknowledgment />
        </Stack>
      </DialogBody>
      {!isLoading && (
        <DialogFooter>
          <Stack direction="row" gap="sm" justify="end">
            <Button
              disabled={status !== 'ready'}
              emphasis="heavy"
              fluid={{ base: true, sm: false }}
              loading={status === 'submitting'}
              onClick={submit}
              size={{ base: 'lg', sm: 'md' }}
              tone="accent"
            >
              {hasExistingPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
            </Button>
          </Stack>
        </DialogFooter>
      )}
    </>
  );
};
